import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwBadge } from '../../shared/ui/badge/uw-badge';
import { UwSkeleton } from '../../shared/ui/skeleton/uw-skeleton';
import { UwAccountHeader } from '../../shared/domain/account-header/uw-account-header';
import { UwAiNarrative } from '../../shared/domain/ai-narrative/uw-ai-narrative';
import { UwEvidencePanel } from '../../shared/domain/evidence-panel/uw-evidence-panel';
import { UwAiActionHub } from '../../shared/domain/ai-action-hub/uw-ai-action-hub';
import { UwRiskScore } from '../../shared/domain/risk-score/uw-risk-score';
import { UwRiskFactor } from '../../shared/domain/risk-factor/uw-risk-factor';
import { AccountStore } from '../../core/stores/account.store';
import { EvidenceStore } from '../../core/stores/evidence.store';
import { CopilotService } from '../../core/services/copilot.service';
import { RISK_ASSESSMENTS } from '../../core/services/mock-data';
import type { Account } from '../../core/models/account.model';
import type { AiNarrative } from '../../core/models/ai.model';
import type { RiskAssessment, RiskFactor } from '../../core/models/risk.model';

@Component({
  selector: 'uw-copilot-account-page',
  standalone: true,
  imports: [UwIcon, UwSkeleton, UwAccountHeader, UwAiNarrative, UwEvidencePanel, UwAiActionHub, UwRiskScore, UwRiskFactor],
  template: `
    <div class="h-full overflow-y-auto">
      <div class="mx-auto max-w-[1440px] space-y-4 p-4">
        <uw-account-header [account]="account()" />

        <div class="grid grid-cols-1 gap-4 xl:grid-cols-[1.55fr_1fr]">
          <div class="space-y-4">
            <uw-ai-narrative [narrative]="narrative()" [loading]="narrativeLoading()" />

            <section class="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
              @if (assessment(); as a) {
                <div class="border-b border-line bg-surface-sunken/40">
                  <uw-risk-score [assessment]="a" />
                </div>
                <div class="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
                  <div class="space-y-3">
                    <p class="typo-label">Factor scores</p>
                    @for (factor of a.factors; track factor.label) {
                      <uw-risk-factor [factor]="factor" />
                    }
                  </div>
                  <div class="space-y-3">
                    <p class="typo-label">Engine recommendation</p>
                    <p class="flex items-start gap-1.5 rounded-lg border border-success/25 bg-success-soft/50 px-3 py-2.5 text-[11.5px] leading-relaxed text-secondary">
                      <uw-icon name="check-circle" [size]="13" class="mt-0.5 shrink-0 text-success" />
                      {{ a.recommendation }}
                    </p>
                    <p class="typo-label">Peer benchmark</p>
                    <dl class="divide-y divide-line rounded-lg border border-line">
                      @for (row of a.benchmark; track row.label) {
                        <div class="flex items-center justify-between gap-2 px-3 py-1.5 text-[11.5px]">
                          <dt class="text-secondary">{{ row.label }}</dt>
                          <dd class="flex items-center gap-1.5">
                            <span class="font-semibold text-primary">{{ row.account }}</span>
                            <span class="text-text-muted">vs</span>
                            <span class="text-text-muted">{{ row.peer }}</span>
                          </dd>
                        </div>
                      }
                    </dl>
                  </div>
                </div>
              } @else {
                <div class="space-y-2 p-4">
                  <uw-skeleton height="14px" width="60%" />
                  <uw-skeleton height="14px" width="85%" />
                  <uw-skeleton height="14px" width="70%" />
                </div>
              }
            </section>
          </div>

          <div class="flex flex-col gap-4">
            <section class="flex min-h-[420px] flex-col rounded-xl border border-line bg-surface shadow-sm">
              <div class="min-h-0 flex-1 overflow-hidden p-3.5">
                <uw-evidence-panel [loading]="evidenceLoading()" />
              </div>
            </section>
            <section class="flex min-h-[300px] flex-col rounded-xl border border-line bg-surface shadow-sm">
              <div class="min-h-0 flex-1 overflow-y-auto p-3.5">
                <uw-ai-action-hub />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class UwCopilotAccountPage {
  private readonly route = inject(ActivatedRoute);
  private readonly accountStore = inject(AccountStore);
  private readonly evidenceStore = inject(EvidenceStore);
  private readonly copilotService = inject(CopilotService);

  protected readonly account = computed<Account | null>(() => this.accountStore.selectedAccount() ?? null);
  protected readonly assessment = computed<RiskAssessment | null>(() => (this.account() ? this.derive(this.account()!) : null));

  protected readonly narrative = signal<AiNarrative | null>(null);
  protected readonly narrativeLoading = signal(true);
  protected readonly evidenceLoading = signal(true);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id') ?? '';
      if (id && id !== this.accountStore.selectedAccountId()) {
        this.accountStore.selectAccount(id);
      }
      this.load(id || this.accountStore.selectedAccountId());
    });
  }

  private load(accountId: string): void {
    this.narrativeLoading.set(true);
    this.evidenceLoading.set(true);
    this.evidenceStore.load(accountId);
    this.copilotService.getNarrative(accountId).pipe(take(1)).subscribe((n) => {
      this.narrative.set(n);
      this.narrativeLoading.set(false);
    });
    setTimeout(() => this.evidenceLoading.set(false), 350);
  }

  private derive(account: Account): RiskAssessment {
    const existing = RISK_ASSESSMENTS[account.id];
    if (existing) return existing;
    const factors: RiskFactor[] = [
      {
        label: 'Financial stability (D&B)',
        score: Math.min(95, Math.max(40, Math.round(account.annualRevenue / 1_000_000 + 60))),
        tone: account.annualRevenue > 25_000_000 ? 'success' : 'warning',
      },
      { label: 'Loss experience (5yr)', score: Math.max(20, 100 - account.lossRatio), tone: account.lossRatio < 40 ? 'success' : 'warning' },
      {
        label: 'Industry hazard class',
        score: account.riskScore >= 60 ? 70 : 50,
        tone: account.riskScore >= 60 ? 'success' : 'warning',
      },
      {
        label: 'Safety & controls',
        score: account.riskScore >= 70 ? 80 : 55,
        tone: account.riskScore >= 70 ? 'success' : 'warning',
      },
    ];
    return {
      accountId: account.id,
      compositeScore: account.riskScore,
      riskLabel: account.riskLabel,
      scoreTone: account.riskScore >= 70 ? 'success' : account.riskScore >= 55 ? 'warning' : 'danger',
      updated: 'recomputed',
      factors,
      narrative: `Derived assessment for ${account.name}.`,
      recommendation:
        account.riskScore >= 70
          ? 'Proceed to pricing at standard terms within authority.'
          : 'Proceed with caution — obtain additional documentation before binding.',
      benchmark: account.peerBenchmark ?? [],
    };
  }
}
